import { useEffect, useState } from 'react'
import { Download, Trash2 } from 'lucide-react'
import { materialsApi } from '@/api/materials'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import type { Material } from '@/types/material'
import { MaterialThumbnail } from './MaterialThumbnail'

interface MaterialGridProps {
	materials: Material[]
	canDelete: boolean
	onDownload: (id: number) => void
	onDelete: (id: number) => void
}

export function MaterialGrid({ materials, canDelete, onDownload, onDelete }: MaterialGridProps) {
	const [preview, setPreview] = useState<{ material: Material; url: string } | null>(null)

	useEffect(() => {
		return () => {
			if (preview?.url) URL.revokeObjectURL?.(preview.url)
		}
	}, [preview])

	const openPreview = async (item: Material) => {
		if (!item.mime.startsWith('image/')) return
		const url = await materialsApi.preview(item.id)
		setPreview((current) => {
			if (current?.url) URL.revokeObjectURL?.(current.url)
			return { material: item, url }
		})
	}

	if (materials.length === 0) {
		return (
			<div className="flex min-h-60 items-center justify-center rounded-md border border-dashed bg-muted/20 text-sm text-muted-foreground">
				暂无素材
			</div>
		)
	}

	return (
		<>
			<div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
				{materials.map((item) => (
					<div key={item.id} className="rounded-md border bg-background p-3">
						<button
							type="button"
							aria-label={`预览 ${item.original_name}`}
							disabled={!item.mime.startsWith('image/')}
							onClick={() => void openPreview(item)}
							className="mb-2 flex aspect-square w-full items-center justify-center overflow-hidden rounded-md bg-muted text-sm text-muted-foreground disabled:cursor-default"
						>
							<MaterialThumbnail material={item} className="h-full w-full rounded-md object-cover" />
						</button>
						<p className="truncate text-sm font-medium" title={item.original_name}>
							{item.original_name}
						</p>
						<p className="text-xs text-muted-foreground">{formatBytes(item.size)}</p>
						<div className="mt-3 flex gap-2">
							<Button type="button" size="sm" variant="outline" onClick={() => onDownload(item.id)}>
								<Download className="mr-1.5 h-3.5 w-3.5" />
								下载
							</Button>
							{canDelete ? (
								<Button type="button" size="sm" variant="destructive" onClick={() => onDelete(item.id)}>
									<Trash2 className="mr-1.5 h-3.5 w-3.5" />
									删除
								</Button>
							) : null}
						</div>
					</div>
				))}
			</div>
			<Dialog open={preview !== null} onOpenChange={(open) => !open && setPreview(null)}>
				<DialogContent className="max-h-[92vh] w-[calc(100vw-2rem)] max-w-5xl overflow-hidden p-4">
					<DialogTitle className="truncate pr-8 text-base">{preview?.material.original_name}</DialogTitle>
					<DialogDescription className="sr-only">查看素材原图</DialogDescription>
					{preview ? (
						<div className="flex min-h-0 items-center justify-center overflow-auto rounded-md bg-muted/40">
							<img src={preview.url} alt={preview.material.original_name} className="max-h-[78vh] max-w-full object-contain" />
						</div>
					) : null}
				</DialogContent>
			</Dialog>
		</>
	)
}

function formatBytes(size: number) {
	if (size < 1024) return `${size} B`
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
	return `${(size / 1024 / 1024).toFixed(1)} MB`
}
